import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { xor } from 'lodash';
import { IngredientModal, useIngredientModal } from '../../components';
import { ProductSelectionForm } from '../ProductSelectionForm';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import {
  recipeSelected,
  recipeUnselected,
  recipesShown,
  userContinuesToCheckoutEvent,
  userViewFreshRecipeScreen,
  userExpandsFreshRecipeDetails,
} from './events';

export const FreshRecipesSelectionForm = ({
  onSubmit,
  headline,
  subheadline,
  modals,
  ctaText,
  recipes,
  defaultRecipes,
  unavailableRecipes,
  returnToQuiz,
}) => {
  const [selectedRecipes, setSelectedRecipes] = useState(defaultRecipes);
  const { modal, onRecipeDetailsClick } = useIngredientModal(modals);
  const reporter = useReporter();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const availableRecipes = recipes.filter(
    ({ value }) => !unavailableRecipes.includes(value),
  );

  useEffect(() => {
    reporter.tag(userViewFreshRecipeScreen(availableRecipes.length));
  }, [reporter, availableRecipes.length]);

  useEffect(() => {
    const presentedRecipes = recipes.map(recipe => recipe.value);
    const unselectedRecipes = xor(defaultRecipes, presentedRecipes);
    reporter.tag(
      recipesShown(defaultRecipes, unselectedRecipes, unavailableRecipes),
    );
  }, [defaultRecipes, recipes, reporter, unavailableRecipes]);

  const onChange = recipes => {
    const recipeAdded = recipes.length > selectedRecipes.length;
    const recipe = xor(recipes, selectedRecipes)[0];
    const event = recipeAdded ? recipeSelected : recipeUnselected;
    reporter.tag(event(recipe));
    setSelectedRecipes(recipes);
  };

  const isValid = selectedRecipes.length > 0;
  const noSelectedRecipes = selectedRecipes.length === 0;

  const onSubmitHandler = async event => {
    event.preventDefault();
    if (!isValid) {
      return;
    }
    setIsSubmitting(true);
    const position = 'footer';
    reporter.tag(userContinuesToCheckoutEvent(position));
    const submitResult = await onSubmit(selectedRecipes);
    ApiResult.ifError(submitResult, () => setIsSubmitting(false));
  };

  const sortedSelectedRecipes = noSelectedRecipes ? [] : selectedRecipes;

  const checkoutBarProps = {
    ctaText,
    disabled: !isValid || isSubmitting,
    shown: true,
    onBackButtonClick: returnToQuiz,
  };

  const legend = `Choose up to ${recipes.length} recipe${
    recipes.length > 1 ? 's' : ''
  }`;
  const onRecipeDetailsHandler = product => {
    reporter.tag(userExpandsFreshRecipeDetails(product.value));
    onRecipeDetailsClick(product);
  };
  const productSelectorProps = {
    onDetailsClick: onRecipeDetailsHandler,
    products: recipes,
    selected: sortedSelectedRecipes,
    unavailableProducts: unavailableRecipes,
  };

  return (
    <>
      <ProductSelectionForm
        checkoutBar={checkoutBarProps}
        headline={headline}
        legend={legend}
        onChange={onChange}
        onSubmit={onSubmitHandler}
        productSelector={productSelectorProps}
        subheadline={subheadline}
      />
      <IngredientModal {...modal} />
    </>
  );
};
FreshRecipesSelectionForm.displayName = 'FreshRecipesSelectionForm';
FreshRecipesSelectionForm.propTypes = {
  modals: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  headline: PropTypes.string.isRequired,
  subheadline: PropTypes.node.isRequired,
  ctaText: PropTypes.string.isRequired,
  unavailableRecipes: PropTypes.array,
  recipes: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      description: PropTypes.node.isRequired,
      img: PropTypes.any,
    }),
  ),
  defaultRecipes: PropTypes.arrayOf(PropTypes.string).isRequired,
  returnToQuiz: PropTypes.func.isRequired,
};
